import { PasoTags, PriorityLevel } from '../types/api';

// Canonical sentinel for "too large to estimate" (semantic +infinity over JSON).
export const HUGE_ESTIMATED_TIME_TO_DONE_MINUTES = 2147483647;

export type PriorityKind = 'cost' | 'value';

export interface PriorityDisplay {
  label: string;
  color: string;
  shape: 'up' | 'flat' | 'down';
}

const PRIORITY_DISPLAY_MAP: Record<PriorityLevel, PriorityDisplay> = {
  high: {
    label: 'High',
    color: '#ef6c00',
    shape: 'up',
  },
  moderate: {
    label: 'Moderate',
    color: '#f9a825',
    shape: 'flat',
  },
  low: {
    label: 'Low',
    color: '#1e88e5',
    shape: 'down',
  },
};

export interface CategoryTreeNode {
  label: string;
  fullPath: string;
  children: CategoryTreeNode[];
}

export interface TagEditorState {
  enabled: boolean;
  categoriesInput: string;
  dueDate: string;
  isHuge: boolean;
  estimatedTimeValue: string;
  estimatedTimeUnit: '' | 'minutes' | 'hours';
  opportunityCostIfNotDone: '' | PriorityLevel;
  valueIfDone: '' | PriorityLevel;
}

export function parseCategoriesInput(input: string): string[] {
  const pieces = input
    .split(/[,\n]/)
    .map(part => part.trim())
    .filter(Boolean);

  const seen = new Set<string>();
  const categories: string[] = [];
  pieces.forEach(path => {
    const key = path.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    categories.push(path);
  });

  return categories;
}

export function toTagEditorState(tags?: PasoTags): TagEditorState {
  const categories = tags?.categories || [];
  const minutes = tags?.special?.estimatedTimeToDoneMinutes;
  const isHuge = minutes === HUGE_ESTIMATED_TIME_TO_DONE_MINUTES;

  let estimatedTimeValue = '';
  let estimatedTimeUnit: '' | 'minutes' | 'hours' = 'minutes';
  if (isHuge) {
    estimatedTimeUnit = '';
  } else if (typeof minutes === 'number' && Number.isFinite(minutes) && minutes >= 0) {
    if (minutes % 60 === 0 && minutes >= 60) {
      estimatedTimeValue = String(minutes / 60);
      estimatedTimeUnit = 'hours';
    } else {
      estimatedTimeValue = String(minutes);
    }
  }

  return {
    enabled: !!tags,
    categoriesInput: categories.join(', '),
    dueDate: tags?.special?.dueDate || '',
    isHuge,
    estimatedTimeValue,
    estimatedTimeUnit,
    opportunityCostIfNotDone: tags?.special?.opportunityCostIfNotDone || '',
    valueIfDone: tags?.special?.valueIfDone || '',
  };
}

export function buildTagsFromEditor(state: TagEditorState): PasoTags | null {
  if (!state.enabled) {
    return null;
  }

  const categories = parseCategoriesInput(state.categoriesInput);

  let estimatedMinutes: number | undefined;
  if (state.isHuge) {
    estimatedMinutes = HUGE_ESTIMATED_TIME_TO_DONE_MINUTES;
  } else if (state.estimatedTimeValue.trim() !== '') {
    const parsed = Number(state.estimatedTimeValue);
    if (Number.isFinite(parsed) && parsed >= 0) {
      estimatedMinutes = state.estimatedTimeUnit === 'hours' ? Math.round(parsed * 60) : Math.round(parsed);
    }
  }

  const special: PasoTags['special'] = {};
  if (estimatedMinutes !== undefined) {
    special.estimatedTimeToDoneMinutes = estimatedMinutes;
  }
  if (state.dueDate) {
    special.dueDate = state.dueDate;
  }
  if (state.opportunityCostIfNotDone) {
    special.opportunityCostIfNotDone = state.opportunityCostIfNotDone;
  }
  if (state.valueIfDone) {
    special.valueIfDone = state.valueIfDone;
  }

  const hasSpecial = Object.keys(special).length > 0;
  const hasCategories = categories.length > 0;

  if (!hasSpecial && !hasCategories) {
    return null;
  }

  return {
    ...(hasCategories ? { categories } : {}),
    ...(hasSpecial ? { special } : {}),
  };
}

export function formatEstimatedTime(minutes?: number): string {
  if (minutes === undefined || minutes === null) return '';
  if (minutes === HUGE_ESTIMATED_TIME_TO_DONE_MINUTES) {
    return 'Is Huge';
  }
  if (minutes % 60 === 0 && minutes >= 60) {
    return `${minutes / 60}h`;
  }
  return `${minutes}m`;
}

export function getDaysRemainingUntilDue(dueDate?: string): number | null {
  if (!dueDate) return null;

  const parts = dueDate.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) {
    return null;
  }

  const [year, month, day] = parts;
  const dueAt = new Date(year, month - 1, day);
  if (
    dueAt.getFullYear() !== year ||
    dueAt.getMonth() !== month - 1 ||
    dueAt.getDate() !== day
  ) {
    return null;
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((dueAt.getTime() - today.getTime()) / msPerDay);
}

export function formatDueDateDaysRemaining(dueDate?: string): string {
  const daysRemaining = getDaysRemainingUntilDue(dueDate);
  if (daysRemaining === null) return '';
  return `${daysRemaining} days`;
}

export function getPriorityChipLabel(kind: PriorityKind): string {
  return kind === 'cost' ? 'Neglect Cost' : 'Value';
}

export function getPriorityDisplay(value?: PriorityLevel): PriorityDisplay | null {
  if (!value) return null;
  return PRIORITY_DISPLAY_MAP[value];
}

export function getCategorySegments(path: string): string[] {
  return path.split(':').map(p => p.trim()).filter(Boolean);
}

export function buildCategoryTree(paths: string[]): CategoryTreeNode[] {
  type MutableCategoryTreeNode = CategoryTreeNode & {
    childIndex: Map<string, MutableCategoryTreeNode>;
  };

  const rootNodes: MutableCategoryTreeNode[] = [];
  const rootIndex = new Map<string, MutableCategoryTreeNode>();

  paths.forEach(path => {
    const segments = getCategorySegments(path);
    if (segments.length === 0) {
      return;
    }

    let currentNodes = rootNodes;
    let currentIndex = rootIndex;
    const currentPath: string[] = [];

    segments.forEach(segment => {
      currentPath.push(segment);
      const key = segment.toLowerCase();
      let node = currentIndex.get(key);

      if (!node) {
        node = {
          label: segment,
          fullPath: currentPath.join(':'),
          children: [],
          childIndex: new Map<string, MutableCategoryTreeNode>(),
        };
        currentIndex.set(key, node);
        currentNodes.push(node);
      }

      currentNodes = node.children as MutableCategoryTreeNode[];
      currentIndex = node.childIndex;
    });
  });

  const finalizeTree = (nodes: MutableCategoryTreeNode[]): CategoryTreeNode[] => {
    return [...nodes]
      .sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }))
      .map(({ childIndex, children, ...node }) => ({
        ...node,
        children: finalizeTree(children as MutableCategoryTreeNode[]),
      }));
  };

  return finalizeTree(rootNodes);
}
